import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import Slider from "react-slick";

import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics";

import {
    type PartnerTemplate,
    PartnerTemplateCard
} from "./partner-template-card";
import { ConfluenceTemplateMessages } from "./template-messages";

import type { Settings as SlickSettings } from "react-slick";

// Source https://github.com/kenwheeler/slick/blob/master/slick/slick.css
const slickStyle = css({
    ".slick-slider": {
        position: "relative",
        display: "block",
        boxSizing: "border-box",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        MsUserSelect: "none",
        userSelect: "none",
        WebkitTouchCallout: "none",
        KhtmlUserSelect: "none",
        MsTouchAction: "pan-y",
        touchAction: "pan-y",
        WebkitTapHighlightColor: "transparent"
    },
    ".slick-list": {
        position: "relative",
        display: "block",
        overflow: "hidden",
        margin: 0,
        padding: 0
    },
    ".slick-list:focus": {
        outline: "none"
    },
    ".slick-list.dragging": {
        cursor: "pointer"
    },
    ".slick-slider .slick-track, .slick-slider .slick-list": {
        WebkitTransform: "translate3d(0, 0, 0)",
        MozTransform: "translate3d(0, 0, 0)",
        MsTransform: "translate3d(0, 0, 0)",
        OTransform: "translate3d(0, 0, 0)",
        transform: "translate3d(0, 0, 0)"
    },
    ".slick-track": {
        position: "relative",
        insetBlockStart: 0,
        insetInlineStart: 0,
        display: "block",
        marginInlineStart: "auto",
        marginInlineEnd: "auto"
    },
    ".slick-track:before, .slick-track:after": {
        display: "table",
        content: "''"
    },
    ".slick-track:after": {
        clear: "both"
    },
    ".slick-loading .slick-track": {
        visibility: "hidden"
    },
    ".slick-slide": {
        display: "none",
        float: "left",
        height: "100%",
        minHeight: "1px"
    },
    "[dir='rtl'] .slick-slide": {
        float: "right"
    },
    ".slick-slide img": {
        display: "block"
    },
    ".slick-slide.slick-loading img": {
        display: "none"
    },
    ".slick-slide.dragging img": {
        pointerEvents: "none"
    },
    ".slick-initialized .slick-slide": {
        display: "block"
    },
    ".slick-loading .slick-slide": {
        visibility: "hidden"
    },
    ".slick-vertical .slick-slide": {
        display: "block",
        height: "auto",
        border: "1px solid transparent"
    },
    ".slick-arrow.slick-hidden": {
        display: "none"
    }
});

const carouselContainer = css({
    "position": "relative",
    "width": "960px",
    "margin": "0 auto",
    "@media (max-width: 539.99px)": {
        width: "328px",
        height: "480px"
    },
    "@media (min-width: 540px) and (max-width: 1023.99px)": {
        width: "592px",
        margin: "0 auto"
    },
    "@media (min-width: 1024px) and (max-width: 1279.99px)": {
        width: "900px"
    }
});
const carouselCardWrapper = css({
    display: "inline-block",
    width: "100%"
});

const buttonLeft = css({
    "display": "block",
    "position": "absolute",
    "insetBlockStart": "50%",
    "width": 32,
    "height": 32,
    "border": "0 none",
    "transform": "translateY(-60%)",
    "borderRadius": "50%",
    "outline": "none",
    "cursor": "pointer",
    "color": "#42526e",
    "lineHeight": 28,
    "insetInlineStart": -72,
    "backgroundColor": "transparent",

    "@media (max-width: 1023.99px)": {
        insetInlineStart: 0,
        zIndex: 1,
        backgroundColor: "#ffffff",
        boxShadow: "1px 1px 6px 0 rgba(0,0,0,.2)"
    }
});

const buttonRight = css({
    "display": "block",
    "position": "absolute",
    "insetBlockStart": "50%",
    "width": 32,
    "height": 32,
    "border": "0 none",
    "transform": "translateY(-60%)",
    "borderRadius": "50%",
    "outline": "none",
    "cursor": "pointer",
    "color": "#42526e",
    "lineHeight": 28,
    "insetInlineEnd": -72,
    "backgroundColor": "transparent",
    "@media (max-width: 1023.99px)": {
        insetInlineEnd: 0,
        zIndex: 1,
        backgroundColor: "#ffffff",
        boxShadow: "1px 1px 6px 0 rgba(0,0,0,.2)"
    }
});

const buttonLeftArrow = css({
    transform: "rotate(-45deg)",
    width: "12px",
    height: "12px",
    lineHeight: "0px",
    position: "absolute",
    marginBlockStart: "-6px",
    borderBlockStart: "3px solid",
    borderInlineStart: "3px solid",
    borderBlockLeft: "3px solid",
    color: "#42526e",
    fontSize: "16px"
});

const buttonRightArrow = css({
    transform: "rotate(135deg)",
    width: "12px",
    height: "12px",
    lineHeight: "0px",
    position: "absolute",
    marginBlockStart: "-6px",
    borderBlockStart: "3px solid",
    borderInlineStart: "3px solid",
    color: "#42526e",
    fontSize: "16px"
});

const buttonRoundedArrow = css({
    width: "12px",
    height: "12px",
    display: "inline-block",
    lineHeight: "1px",
    position: "absolute",
    marginBlockStart: "-6px",
    boxSizing: "border-box",
    borderBlockStart: "3px solid",
    borderInlineStart: "3px solid",
    borderBlockLeft: "3px solid",
    color: "#0052cc",
    fontSize: "16px"
});

const buttonRoundedLeftArrow = css({
    transform: "rotate(-45deg)",
    marginInlineStart: -4
});

const buttonRoundedRightArrow = css({
    transform: "rotate(135deg)",
    marginInlineStart: -8
});

export interface PartnerTemplateCardCarouselProps {
    partnerTemplates: PartnerTemplate[];
}

export const PartnerTemplateCardCarousel = ({
    partnerTemplates
}: PartnerTemplateCardCarouselProps) => {
    const intl = useIntl();
    const previousLabel = intl.formatMessage(
        ConfluenceTemplateMessages.carouselPreviousLabel
    );
    const nextLabel = intl.formatMessage(
        ConfluenceTemplateMessages.carouselNextLabel
    );

    const LeftArrow = ({ onClick }: { onClick?: () => void }) => (
        <button
            css={buttonLeft}
            onClick={onClick}
            className="doNotAutoTrack"
            aria-label={previousLabel}
        >
            <span css={buttonLeftArrow} />
        </button>
    );
    const RightArrow = ({ onClick }: { onClick?: () => void }) => (
        <button
            css={buttonRight}
            onClick={onClick}
            className="doNotAutoTrack"
            aria-label={nextLabel}
        >
            <span css={buttonRightArrow} />
        </button>
    );

    const LeftArrowRounded = ({ onClick }: { onClick?: () => void }) => (
        <button
            css={buttonLeft}
            onClick={onClick}
            className="doNotAutoTrack"
            aria-label={previousLabel}
        >
            <span css={[buttonRoundedArrow, buttonRoundedLeftArrow]} />
        </button>
    );

    const RightArrowRounded = ({ onClick }: { onClick?: () => void }) => (
        <button
            css={buttonRight}
            onClick={onClick}
            className="doNotAutoTrack"
            aria-label={nextLabel}
        >
            <span css={[buttonRoundedArrow, buttonRoundedRightArrow]} />
        </button>
    );

    const settings: SlickSettings = {
        prevArrow: <LeftArrow />,
        nextArrow: <RightArrow />,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    prevArrow: <LeftArrowRounded />,
                    nextArrow: <RightArrowRounded />
                }
            }
        ]
    };
    const beforeChange = (currentSlide: number, nextSlide: number) => {
        ConfluenceTemplateAnalytics.sendPartnerCarouselAnalytic(
            currentSlide,
            nextSlide,
            partnerTemplates.length
        );
    };

    return (
        <div css={carouselContainer}>
            <div css={slickStyle}>
                <Slider {...settings} beforeChange={beforeChange}>
                    {partnerTemplates.map((partnerTemplate) => (
                        <div
                            css={carouselCardWrapper}
                            key={partnerTemplate.templateId}
                        >
                            <PartnerTemplateCard template={partnerTemplate} />
                        </div>
                    ))}
                </Slider>
            </div>
        </div>
    );
};
